import { Rewind, Play, StepForward, StepBack, Clock } from 'lucide-react';
import {
  STAGE_LABELS,
  INCIDENT_STAGES,
  stageIndex,
  stagesForType,
  activeIncidentTypes,
  elapsedSec,
  formatElapsed,
  type Incident,
  type IncidentStage,
} from '@duty/shared';
import { useIncidentStore } from '@/incident/store';
import { useNow } from '@/hooks/useNow';

/** Stage stepper + incident-clock controls (real / scrub / step). */
export function StageStepper({ incident }: { incident: Incident }) {
  const { dispatch } = useIncidentStore();
  const now = useNow();
  const elapsed = elapsedSec(incident.clock, now);
  const scrubbing = incident.clock.mode === 'scrub';

  // Union the stage profiles of every currently-active type (primary + any
  // co-active additional types), so a stage only relevant to a type added
  // mid-incident (e.g. Suppression once a car fire spreads into grass) is
  // reachable without needing to reclassify the primary type. Sorted back
  // into canonical stage order, not concatenated per-type.
  const activeTypeStages = new Set(activeIncidentTypes(incident).flatMap((t) => stagesForType(t)));
  const typeStages = INCIDENT_STAGES.filter((s) => activeTypeStages.has(s));
  const currentIdx = typeStages.indexOf(incident.stage);

  function prevTypeStage(): IncidentStage {
    return typeStages[Math.max(currentIdx - 1, 0)] ?? incident.stage;
  }
  function nextTypeStage(): IncidentStage {
    return typeStages[Math.min(currentIdx + 1, typeStages.length - 1)] ?? incident.stage;
  }

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border bg-surface/60 px-4 py-2">
      {/* Stages — only those relevant to this incident type */}
      <ol className="flex flex-1 flex-wrap items-center gap-1" aria-label="Incident stage">
        {typeStages.map((stage) => {
          // Use global stageIndex so minStage comparisons remain consistent.
          const globalCurrent = stageIndex(incident.stage);
          const globalThis = stageIndex(stage);
          const state =
            globalThis < globalCurrent ? 'done' : globalThis === globalCurrent ? 'current' : 'todo';
          return (
            <li key={stage}>
              <button
                onClick={() => dispatch({ type: 'set-stage', stage })}
                aria-current={state === 'current' ? 'step' : undefined}
                className={`rounded-md px-2 py-1 text-xs transition-colors ${
                  state === 'current'
                    ? 'bg-accent/20 font-semibold text-accent'
                    : state === 'done'
                      ? 'text-text/70 hover:text-text'
                      : 'text-muted hover:text-text'
                }`}
              >
                {STAGE_LABELS[stage]}
              </button>
            </li>
          );
        })}
      </ol>

      {/* Clock controls */}
      <div className="flex items-center gap-1.5">
        <span
          className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono text-sm ${
            scrubbing ? 'bg-med/15 text-med' : 'bg-surface-2 text-text'
          }`}
          title={scrubbing ? 'Scrub (simulated) time' : 'Real time'}
        >
          <Clock size={13} aria-hidden />
          {formatElapsed(elapsed)}
        </span>
        <IconBtn label="Step back 1 min" onClick={() => dispatch({ type: 'clock-step', deltaSec: -60 })}>
          <StepBack size={15} />
        </IconBtn>
        <IconBtn label="Step forward 1 min" onClick={() => dispatch({ type: 'clock-step', deltaSec: 60 })}>
          <StepForward size={15} />
        </IconBtn>
        <IconBtn
          label="Previous stage"
          onClick={() => dispatch({ type: 'set-stage', stage: prevTypeStage() })}
        >
          <Rewind size={15} />
        </IconBtn>
        <IconBtn
          label="Next stage"
          onClick={() => dispatch({ type: 'set-stage', stage: nextTypeStage() })}
        >
          <Play size={15} />
        </IconBtn>
        {scrubbing && (
          <button
            onClick={() => dispatch({ type: 'clock-real' })}
            className="rounded-md border border-border px-2 py-1 text-xs text-muted hover:text-accent"
          >
            Go live
          </button>
        )}
      </div>
    </div>
  );
}

function IconBtn({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className="rounded-md border border-border bg-surface-2 p-1.5 text-muted hover:text-accent"
    >
      {children}
    </button>
  );
}
